import Vue from 'vue'
import Vuex from 'vuex'
import backend from './api'

Vue.use(Vuex)

function timeToSeconds (time) {
  if (typeof time === 'number') { return time }
  let parts = time.split(':')
  return (+parts[0]) * 60 + (+parts[1])
}

export function createStore (providerIdentifier, sessionIdentifier) {
  if (providerIdentifier && sessionIdentifier) {
    console.log(`[INIT] user identified as ${providerIdentifier}/${sessionIdentifier}`)
  }
  return new Vuex.Store({
    state: {
      playing: false,
      loading: true,
      error: null,
      bookSuccessfullyReturned: false,
      initialTime: 0,
      currentTime: 0,
      current: 0,
      progress: null,
      playbackRate: 1,
      volume: 0.5,
      totalTracks: 0,
      manifest: { metadata: {}, spine: [], links: [] },
      sessionIdentifier: sessionIdentifier,
      providerIdentifier: providerIdentifier
    },

    getters: {
      longIdentifier: () => location.pathname.substring(1),
      shortIdentifier: state => state.manifest.metadata.identifier,
      loading: state => state.loading,
      playing: state => state.playing,
      error: state => state.error,
      bookSuccessfullyReturned: state => state.bookSuccessfullyReturned,
      book: state => state.manifest,
      currentItem: state => state.manifest.spine[state.current || 0],
      currentTime: state => state.currentTime,
      initialTime: state => state.initialTime,
      progress: state => state.progress,
      playbackRate: state => state.playbackRate,
      volume: state => state.volume,
      // Obtain the total duration of the audiobook, in seconds
      totalDuration: state => {
        return state.manifest.spine.reduce((sum, segment) => {
          return sum + timeToSeconds(segment.duration)
        }, 0)
      },
      // Obtain the overall reading position in the book, as a number between 0 and 1
      overallProgress: (state, getters) => {
        let total = getters.totalDuration
        if (total === 0) {
          return 0
        }
        if (state.current === 0) {
          return state.currentTime / total
        }
        let previousItemsDuration = state.manifest.spine.slice(0, state.current).reduce((sum, segment) => sum + timeToSeconds(segment.duration), 0)
        return (previousItemsDuration + state.currentTime) / total
      }
    },

    actions: {
      fetchBook ({ commit, state }) {
        let bookid = location.pathname.substring(1)
        let providerid = state.providerIdentifier
        let sessionid = state.sessionIdentifier
        backend.fetchRemoteManifest(bookid).then(function (manifest) {
          backend.fetchProgress(manifest.metadata.identifier, providerid, sessionid).then(function (progress) {
            console.log(`Progress is at ${progress.p}`);
            backend.setManifest(bookid, manifest)
            return commit('setBookWithProgress', { book: manifest, progress: progress })
          })
        }).catch((err) => commit('setError', err))
      },

      fetchSetting ({ commit }) {
        backend.fetchPlaybackRate().then(value => {
          if (!value) return
          commit('setPlaybackRate', parseFloat(value))
        })

        backend.fetchVolume().then(value => {
          if (!value) return
          commit('setVolume', parseFloat(value))
        })
      },

      returnBook ({ commit, state }, book) {
        let bookid = location.pathname.substring(1)

        // Update the local manifest with the new one.
        // Note: We dont need to update the currently loaded manifest (state.book)
        // because the boolean prop state.bookSuccessfullyReturned is used as a
        // check to load the player or not.
        backend.setManifest(bookid, book, true)
        state.bookSuccessfullyReturned = true
      },

      nextTrack ({ commit }) {
        commit('incrementCurrent')
      },

      previousTrack ({ commit }) {
        commit('decrementCurrent')
      },

      gotoChapter ({ commit }, index) {
        commit('setCurrent', index)
      },

      stop ({ commit }) {
        commit('setPlaying', false)
      },

      setPlaying ({ commit, state, getters }, playing) {
        let fragment = `${state.manifest.spine[state.current].href}#t=${state.currentTime}`
        backend.setProgress(state.manifest.metadata.identifier, state.providerIdentifier, state.sessionIdentifier, fragment, getters.overallProgress, true)
        commit('setPlaying', playing)
      },

      updatePlaybackProgress ({ commit, state, getters }, time) {
        let fragment = `${state.manifest.spine[state.current].href}#t=${time}`
        backend.setProgress(state.manifest.metadata.identifier, state.providerIdentifier, state.sessionIdentifier, fragment, getters.overallProgress)
        return commit('setPlaybackProgress', time)
      },

      quickRewind ({ commit, state }) {
        commit('setPlaybackProgress', state.currentTime - 30)
      },

      quickFastForward ({ commit, state }) {
        commit('setPlaybackProgress', state.currentTime + 30)
      },

      updatePlaybackRate ({ commit }, value) {
        backend.setPlaybackRate(value)
        commit('setPlaybackRate', value)
      },

      updateVolume ({ commit }, value) {
        backend.setVolume(value)
        commit('setVolume', value)
      }
    },

    mutations: {
      setBookWithProgress (state, { book, progress }) {
        state.manifest = book

        state.progress = progress
        if (progress.ts) {
          let parts = progress.ts.split('#t=')
          let spineItem = state.manifest.spine.findIndex(item => item.href === parts[0] || (item.alternates && item.alternates.find(alt => alt.href === parts[0])))
          if (spineItem >= 0) {
            state.current = spineItem
            state.initialTime = +parts[1]
          } else {
            console.log(`Could not find spine item ${parts[0]}`);
          }
        }

        state.loading = false
      },

      incrementCurrent (state) {
        let totalTracks = state.manifest.spine.length
        if (state.current >= (totalTracks - 1)) return
        state.current += 1
      },

      decrementCurrent (state) {
        if (state.current <= 0) return
        state.current -= 1
      },

      setCurrent (state, chapter) {
        state.current = chapter
        state.currentTime = 0
        state.initialTime = 0
      },

      setPlaying (state, playing) {
        state.playing = playing
      },

      setError (state, error) {
        state.error = error
      },

      setPlaybackProgress (state, time) {
        let currentItem = state.manifest.spine[state.current]
        let correctedTime = (function () {
          let duration = currentItem.duration
          if (time < 0) return 0
          if (time > duration) return duration
          return time
        })()

        state.currentTime = correctedTime
        state.progress = `${currentItem.href}#t=${correctedTime.toFixed(4)}`
      },

      setPlaybackRate (state, value) {
        state.playbackRate = value
      },

      setVolume (state, value) {
        state.volume = value
      }
    }
  })
}



// WEBPACK FOOTER //
// ./src/store.js